import {
  CommonHelperFunctions,
  ECardinality,
  ECRUDFormLayoutType,
  EGalleryViewType,
  IAction,
  IDisplayItem,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import { EActionInputType, IResponsiveLayoutSizes } from "@kuika/kuika-cl-model/src/dtos/screen-design.dto";
import React, { Component } from "react";
import { CrudService } from "../../services/crud-service";
import { FormService } from "../../services/form-service";
import { IWithSDProps, withSD } from "../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import "./element-picker-with-action-modal.scss";

interface IActionWithDi {
  action: IAction;
  di: IDisplayItem;
  eventName: string;
}

interface IGalleryViewPropertyNames {
  image: string | null;
  title: string | null;
}

type TLayoutDevice = "desktop" | "tablet" | "mobile";

interface ILayoutDevice {
  layout: TLayoutDevice;
  icon: string;
  label: string;
  testId: string;
  onboardingId: string;
}

interface ILayoutSizeOption {
  value: ECRUDFormLayoutType;
  label: string;
}

interface IReturnFieldOption {
  value: string;
  label: string;
  testId?: string;
}

interface IChoiceProps {
  label: string;
  icon?: string;
  isSelected: boolean;
  isDisabled?: boolean;
  onClick: () => void;
  testId: string;
  onboardingId: string;
}

interface IElementPickerWithActionModalProps {}

interface IElementPickerWithActionModalState {
  selectedElement: "table" | "form" | "gallery-view" | null;
  selectedGalleryViewLayout: EGalleryViewType | null;
  selectedActionInputType: EActionInputType | null;
  isOnHoverTable: boolean;
  isOnHoverForm: boolean;
  isOnHoverParameters: boolean;
  isOnHoverReturnValue: boolean;
  galleryViewPropertyNames: IGalleryViewPropertyNames | null;
  responsiveLayoutSizes: IResponsiveLayoutSizes;
}

class ElementPickerWithActionModal extends Component<
  IElementPickerWithActionModalProps & IWithSDProps,
  IElementPickerWithActionModalState
> {
  static initialState: IElementPickerWithActionModalState = {
    selectedElement: null,
    selectedActionInputType: null,
    isOnHoverTable: false,
    isOnHoverForm: false,
    isOnHoverParameters: false,
    isOnHoverReturnValue: false,
    selectedGalleryViewLayout: null,
    galleryViewPropertyNames: null,
    responsiveLayoutSizes: {
      desktop: ECRUDFormLayoutType.TwoColumn,
      tablet: ECRUDFormLayoutType.TwoColumn,
      mobile: ECRUDFormLayoutType.SingleColumn
    }
  };

  constructor(props: IElementPickerWithActionModalProps & IWithSDProps) {
    super(props);
    this.state = { ...ElementPickerWithActionModal.initialState };
  }

  private onCloseModal = (): void => {
    this.setState({ ...ElementPickerWithActionModal.initialState }, () => {
      this.props.sd.setIsElementPickerWithActionOpen(false);
      this.props.sd.setActionDefinitionForCreateElementForAction(null);
      this.props.sd.setTargetKuikaIdForCreateElementForAction(null);
    });
  };

  private createTableDisplayItem = async (): Promise<void> => {
    if (
      !this.props.sd.targetKuikaIdForCreateElementForAction ||
      !this.props.sd.actionDefinitionForCreateElementForAction?.id
    ) {
      return;
    }

    const actionDefinitionId: string = this.props.sd.actionDefinitionForCreateElementForAction.id;
    const targetKuikaId: string = this.props.sd.targetKuikaIdForCreateElementForAction;

    this.onCloseModal();

    this.props.sd.processWithSpinner(async () => {
      const res = await CrudService.GetTableDi({
        projectId: this.props.sd.project.id,
        actionDefinitionId
      });

      if (!res.data) {
        throw new Error("Table display item can not be null.");
      }

      this.props.sd.injectDiToTargetDi({
        displayItem: res.data,
        targetKuikaId
      });
    });
  };

  private createFormDisplayItem = async (): Promise<void> => {
    if (!this.props.sd.actionDefinitionForCreateElementForAction?.id) {
      throw new Error("Action definition can not be null at this stage.");
    }

    if (
      !this.props.sd.actionDefinitionForCreateElementForAction ||
      !this.props.sd.targetKuikaIdForCreateElementForAction
    ) {
      throw new Error("Action or target kuika id can not be null at this stage.");
    }

    if (this.state.selectedActionInputType === null) {
      throw new Error("Action input type can not be null at this stage.");
    }

    const actionDefinitionId: string = this.props.sd.actionDefinitionForCreateElementForAction.id;
    const targetKuikaId: string = this.props.sd.targetKuikaIdForCreateElementForAction;
    const actionInputType: EActionInputType = this.state.selectedActionInputType;
    const responsiveLayoutSizes: IResponsiveLayoutSizes = {
      ...this.state.responsiveLayoutSizes
    };

    this.onCloseModal();

    this.props.sd.processWithSpinner(async () => {
      const metadataRes = await FormService.GetFormMetadataFromAction({
        actionDefinitionId,
        actionInputType,
        responsiveLayoutSizes
      });

      const res = await FormService.GetFormDiCustom({
        ...metadataRes.data,
        projectId: this.props.sd.project.id
      });

      if (!res.data) {
        throw new Error("Form display item can not be null.");
      }

      this.props.sd.injectDiToTargetDi({
        displayItem: res.data,
        targetKuikaId
      });
    });
  };

  private createGalleryViewDisplayItem = async (): Promise<void> => {
    if (!this.props.sd.actionDefinitionForCreateElementForAction?.id) {
      throw new Error("Action can not be null at this stage.");
    }

    if (
      !this.props.sd.actionDefinitionForCreateElementForAction ||
      !this.props.sd.targetKuikaIdForCreateElementForAction
    ) {
      throw new Error("Action or target kuika id can not be null at this stage.");
    }

    if (this.state.selectedActionInputType === null) {
      throw new Error("Action input type can not be null at this stage.");
    }

    if (this.state.selectedGalleryViewLayout === null) {
      throw new Error("Gallery layout can not be null at this stage.");
    }

    if (!this.state.galleryViewPropertyNames?.image || this.state.galleryViewPropertyNames?.image === "") {
      throw new Error("Gallery view image prop can not be null or empty at this stage.");
    }

    if (
      (this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnSide ||
        this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnBottom) &&
      (!this.state.galleryViewPropertyNames?.title || this.state.galleryViewPropertyNames?.title === "")
    ) {
      throw new Error("Gallery view title prop can not be null or empty at this stage.");
    }

    const actionDefinitionId: string = this.props.sd.actionDefinitionForCreateElementForAction.id;
    const targetKuikaId: string = this.props.sd.targetKuikaIdForCreateElementForAction;
    const galleryViewType: EGalleryViewType = this.state.selectedGalleryViewLayout;
    const imageFieldName: string = this.state.galleryViewPropertyNames.image;
    const titleFieldName: string | null = this.state.galleryViewPropertyNames.title;

    this.onCloseModal();

    this.props.sd.processWithSpinner(async () => {
      const res = await CrudService.GetGalleryViewDi({
        projectId: this.props.sd.project.id,
        imageFieldName,
        titleFieldName,
        galleryViewType,
        actionDefinitionId
      });

      if (!res.data) {
        throw new Error("Form display item can not be null.");
      }

      this.props.sd.injectDiToTargetDi({
        displayItem: res.data,
        targetKuikaId
      });
    });
  };

  private onClickTable = (): void => {
    if (this.state.selectedElement === "table") return;

    this.setState({
      ...ElementPickerWithActionModal.initialState,
      selectedElement: "table",
      selectedActionInputType: EActionInputType.ReturnValue
    });
  };

  private onClickForm = (): void => {
    if (this.state.selectedElement === "form") return;

    this.setState({
      ...ElementPickerWithActionModal.initialState,
      selectedElement: "form",
      selectedActionInputType: null
    });
  };

  private onClickGalleryView = (): void => {
    if (this.state.selectedElement === "gallery-view") return;

    this.setState({
      ...ElementPickerWithActionModal.initialState,
      selectedElement: "gallery-view",
      selectedActionInputType: EActionInputType.ReturnValue
    });
  };

  private onClickCreate = async (): Promise<void> => {
    if (this.state.selectedElement === null) {
      throw new Error("Selected element can not be null at this stage.");
    }

    if (this.state.selectedElement === "table") {
      await this.createTableDisplayItem();
    }

    if (this.state.selectedElement === "form") {
      await this.createFormDisplayItem();
    }

    if (this.state.selectedElement === "gallery-view") {
      await this.createGalleryViewDisplayItem();
    }
  };

  private getIsTableDisabled = (): boolean => {
    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    return (
      this.props.sd.actionDefinitionForCreateElementForAction.returnCardinality !== ECardinality.Many ||
      CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(
        this.props.sd.actionDefinitionForCreateElementForAction.returnTypeName
      )
    );
  };

  private getIsFormDisabled = (): boolean => {
    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    return (
      this.props.sd.actionDefinitionForCreateElementForAction.actionParameters?.length === 0 &&
      CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(
        this.props.sd.actionDefinitionForCreateElementForAction.returnTypeName
      )
    );
  };

  private getIsGalleryViewDisabled = (): boolean => {
    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    return this.props.sd.actionDefinitionForCreateElementForAction.returnCardinality !== ECardinality.Many;
  };

  private getIsParametersDisabled = (): boolean => {
    if (
      !this.props.sd.actionDefinitionForCreateElementForAction ||
      this.state.selectedElement === "gallery-view" ||
      this.state.selectedElement === "table"
    ) {
      return true;
    }

    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    return this.props.sd.actionDefinitionForCreateElementForAction.actionParameters?.length === 0;
  };

  private getIsReturnValueDisabled = (): boolean => {
    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    if (!this.props.sd.actionDefinitionForCreateElementForAction) {
      return true;
    }

    return CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(
      this.props.sd.actionDefinitionForCreateElementForAction.returnTypeName
    );
  };

  private getIsContinueDisabled = (): boolean => {
    if (
      !this.props.sd.actionDefinitionForCreateElementForAction ||
      !this.props.sd.targetKuikaIdForCreateElementForAction ||
      this.state.selectedElement === null
    ) {
      return true;
    }

    if (this.state.selectedElement === "form") {
      return this.state.selectedActionInputType === null;
    }

    if (this.state.selectedElement === "gallery-view") {
      if (this.state.selectedActionInputType === null || this.state.selectedGalleryViewLayout === null) return true;

      if (!this.state.galleryViewPropertyNames) return true;

      if (this.state.selectedGalleryViewLayout === EGalleryViewType.OnlyImage) {
        return !this.state.galleryViewPropertyNames.image || this.state.galleryViewPropertyNames.image === "";
      }

      if (
        this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnSide ||
        this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnBottom
      ) {
        return (
          !this.state.galleryViewPropertyNames.image ||
          this.state.galleryViewPropertyNames.image === "" ||
          !this.state.galleryViewPropertyNames.title ||
          this.state.galleryViewPropertyNames.title === ""
        );
      }
    }

    return false;
  };

  private getActionInstanceLabel(actionWithDi: IActionWithDi): string {
    if (actionWithDi.di.id === SpecialDIIds.rootDI) {
      return `${this.props.sd.getScreenDesign().name} [${actionWithDi.eventName}]`;
    }

    return `${actionWithDi.di.friendlyName} [${actionWithDi.eventName}]`;
  }

  private static readonly LAYOUT_DEVICES: ILayoutDevice[] = [
    {
      layout: "desktop",
      icon: "device-desktop",
      label: "Desktop",
      testId: "desktopLayout",
      onboardingId: "common_element-picker_desktop-layout"
    },
    {
      layout: "tablet",
      icon: "device-tablet",
      label: "Tablet",
      testId: "tabletLayout",
      onboardingId: "common_element-picker_tablet-layout"
    },
    {
      layout: "mobile",
      icon: "device-mobile",
      label: "Mobile",
      testId: "galleryViewLayout",
      onboardingId: "common_element-picker_mobile-layout"
    }
  ];

  private static readonly LAYOUT_SIZE_OPTIONS: ILayoutSizeOption[] = [
    { value: ECRUDFormLayoutType.SingleColumn, label: "Single Column" },
    { value: ECRUDFormLayoutType.TwoColumn, label: "2 Columns" },
    { value: ECRUDFormLayoutType.FourColumn, label: "4 Columns" }
  ];

  private getReturnFieldOptions = (): IReturnFieldOption[] => {
    return (
      this.props.sd.actionDefinitionForCreateElementForAction?.returnTypeModel?.elements?.map(
        (element): IReturnFieldOption => ({ value: element.name, label: element.name })
      ) ?? []
    );
  };

  private onChangeImageProperty = (value: string | number | null | undefined): void => {
    this.setState(
      (
        prevState: IElementPickerWithActionModalState
      ): Pick<IElementPickerWithActionModalState, "galleryViewPropertyNames"> => ({
        galleryViewPropertyNames: {
          image: value === null || value === undefined ? null : String(value),
          title: prevState.galleryViewPropertyNames?.title ?? null
        }
      })
    );
  };

  private onChangeTitleProperty = (value: string | number | null | undefined): void => {
    this.setState(
      (
        prevState: IElementPickerWithActionModalState
      ): Pick<IElementPickerWithActionModalState, "galleryViewPropertyNames"> => ({
        galleryViewPropertyNames: {
          image: prevState.galleryViewPropertyNames?.image ?? null,
          title: value === null || value === undefined ? null : String(value)
        }
      })
    );
  };

  private onSelectGalleryLayout = (layout: EGalleryViewType): void => {
    if (layout === EGalleryViewType.OnlyImage) {
      // An image-only card has no title, so a title picked for another layout is dropped.
      this.setState(
        (
          prevState: IElementPickerWithActionModalState
        ): Pick<IElementPickerWithActionModalState, "selectedGalleryViewLayout" | "galleryViewPropertyNames"> => ({
          selectedGalleryViewLayout: layout,
          galleryViewPropertyNames: {
            image: prevState.galleryViewPropertyNames?.image ?? null,
            title: null
          }
        })
      );
      return;
    }
    this.setState({ selectedGalleryViewLayout: layout });
  };

  private renderLayoutOption = (layout: TLayoutDevice): React.ReactElement => {
    return (
      <KuikaSelectbox
        value={this.state.responsiveLayoutSizes[layout]}
        options={ElementPickerWithActionModal.LAYOUT_SIZE_OPTIONS}
        onChange={(value: ECRUDFormLayoutType): void => {
          this.setState(
            (
              prevState: IElementPickerWithActionModalState
            ): Pick<IElementPickerWithActionModalState, "responsiveLayoutSizes"> => ({
              responsiveLayoutSizes: {
                ...prevState.responsiveLayoutSizes,
                [layout]: value
              }
            })
          );
        }}
      />
    );
  };

  /** One single-select choice: the picked option is the filled button, the rest stay neutral. */
  private renderChoice = (choice: IChoiceProps): React.ReactElement => {
    return (
      <KuikaButton
        key={choice.testId}
        type={choice.isSelected ? "primary" : "secondary"}
        color={choice.isSelected ? "default" : "neutral"}
        disabled={choice.isDisabled}
        onClick={choice.onClick}
        data-testid={choice.testId}
        data-onboarding-id={choice.onboardingId}
      >
        <span className="kuika-create-with-action__choice-label">
          {choice.icon && <KuikaSvg name={choice.icon} width={16} height={16} />}
          {choice.label}
        </span>
      </KuikaButton>
    );
  };

  private renderRow = (label: string, content: React.ReactNode): React.ReactElement => {
    return (
      <div className="kuika-create-with-action__row">
        <span className="kuika-create-with-action__label">{label}</span>
        <div className="kuika-create-with-action__value">{content}</div>
      </div>
    );
  };

  render(): React.ReactElement {
    const returnFieldOptions: IReturnFieldOption[] = this.getReturnFieldOptions();
    // The same fields feed both pickers; each tags its rows so a test can tell which list it is in.
    const imageFieldOptions: IReturnFieldOption[] = returnFieldOptions.map(
      (option: IReturnFieldOption): IReturnFieldOption => ({ ...option, testId: `${option.value}ImagePropertyOption` })
    );
    const titleFieldOptions: IReturnFieldOption[] = returnFieldOptions.map(
      (option: IReturnFieldOption): IReturnFieldOption => ({ ...option, testId: `${option.value}TitlePropertyOption` })
    );
    const isGalleryView: boolean = this.state.selectedElement === "gallery-view";
    const showsTitleProperty: boolean =
      isGalleryView &&
      (this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnSide ||
        this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnBottom);

    const footer: React.ReactElement = (
      <KModalFooter
        onCancel={this.onCloseModal}
        submitLabel="Create"
        submitTestId="createButton"
        submitOnboardingId="common_element-picker_create-button"
        onSubmit={this.onClickCreate}
        disabled={this.getIsContinueDisabled()}
      />
    );

    return (
      <KModal
        header="Create with Action"
        showModal={this.props.sd.isElementPickerWithActionOpen}
        closeModal={this.onCloseModal}
        width={520}
        footer={footer}
        closeTestId="closeModal"
      >
        <div className="kuika-create-with-action">
          <p className="kuika-create-with-action__description">
            Quickly create List, Read, Update and Delete screens from the data table where you added the Datasources
            section.
          </p>
          <div className="kuika-create-with-action__fields">
            {this.renderRow(
              "Source",
              <span className="kuika-create-with-action__source">
                {this.props.sd.actionDefinitionForCreateElementForAction &&
                  KMainFunctions.getFormattedSystemActionName(this.props.sd.actionDefinitionForCreateElementForAction)}
              </span>
            )}
            {this.renderRow(
              "Create",
              <div className="kuika-create-with-action__choices">
                {this.renderChoice({
                  label: "Table",
                  icon: "table-cells",
                  isSelected: this.state.selectedElement === "table",
                  isDisabled: this.getIsTableDisabled(),
                  onClick: this.onClickTable,
                  testId: "tableOption",
                  onboardingId: "common_element-picker_table-option"
                })}
                {this.renderChoice({
                  label: "Form",
                  icon: "form-fields",
                  isSelected: this.state.selectedElement === "form",
                  isDisabled: this.getIsFormDisabled(),
                  onClick: this.onClickForm,
                  testId: "formOption",
                  onboardingId: "common_element-picker_form-option"
                })}
                {this.renderChoice({
                  label: "Gallery View",
                  icon: "form-fields",
                  isSelected: isGalleryView,
                  isDisabled: this.getIsGalleryViewDisabled(),
                  onClick: this.onClickGalleryView,
                  testId: "galleryviewOption",
                  onboardingId: "common_element-picker_galleryview-option"
                })}
              </div>
            )}
            {this.renderRow(
              "Render Type",
              <div className="kuika-create-with-action__choices">
                {this.renderChoice({
                  label: "Inputs",
                  icon: "square-chevron-down",
                  isSelected: this.state.selectedActionInputType === EActionInputType.Parameters,
                  isDisabled: this.getIsParametersDisabled(),
                  onClick: (): void => this.setState({ selectedActionInputType: EActionInputType.Parameters }),
                  testId: "createWithActionInputsButton",
                  onboardingId: "common_element-picker_inputs-option"
                })}
                {this.renderChoice({
                  label: "Outputs",
                  icon: "square-arrow-left",
                  isSelected: this.state.selectedActionInputType === EActionInputType.ReturnValue,
                  isDisabled: this.getIsReturnValueDisabled(),
                  onClick: (): void => this.setState({ selectedActionInputType: EActionInputType.ReturnValue }),
                  testId: "createWithActionOutputsButton",
                  onboardingId: "common_element-picker_outputs-option"
                })}
              </div>
            )}
            {this.state.selectedElement === "form" &&
              this.renderRow(
                "Layout Design",
                <div className="kuika-create-with-action__devices">
                  {ElementPickerWithActionModal.LAYOUT_DEVICES.map((device: ILayoutDevice): React.ReactElement => (
                    <div key={device.layout} className="kuika-create-with-action__device">
                      <KuikaSvg
                        name={device.icon}
                        width={16}
                        height={16}
                        data-testid={device.testId}
                        data-onboarding-id={device.onboardingId}
                      />
                      <span className="kuika-create-with-action__device-name">{device.label}</span>
                      {this.renderLayoutOption(device.layout)}
                    </div>
                  ))}
                </div>
              )}
            {isGalleryView &&
              this.renderRow(
                "Layout Design",
                <div className="kuika-create-with-action__choices">
                  {this.renderChoice({
                    label: "Only Image",
                    isSelected: this.state.selectedGalleryViewLayout === EGalleryViewType.OnlyImage,
                    onClick: (): void => this.onSelectGalleryLayout(EGalleryViewType.OnlyImage),
                    testId: "onlyimageLayout",
                    onboardingId: "common_element-picker_onlyimage-layout"
                  })}
                  {this.renderChoice({
                    label: "Image and Text on Side",
                    isSelected: this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnSide,
                    onClick: (): void => this.onSelectGalleryLayout(EGalleryViewType.ImageAndTextOnSide),
                    testId: "imageandtextonsideLayout",
                    onboardingId: "common_element-picker_image-text-side-layout"
                  })}
                  {this.renderChoice({
                    label: "Image and Text on Bottom",
                    isSelected: this.state.selectedGalleryViewLayout === EGalleryViewType.ImageAndTextOnBottom,
                    onClick: (): void => this.onSelectGalleryLayout(EGalleryViewType.ImageAndTextOnBottom),
                    testId: "imageandtextonbottomLayout",
                    onboardingId: "common_element-picker_image-text-bottom-layout"
                  })}
                </div>
              )}
            {isGalleryView &&
              this.renderRow(
                "Image Property",
                <KuikaSelectbox
                  data-testid="imagePropertyDropdown"
                  data-onboarding-id="common_element-picker_image-property-dropdown"
                  placeholder="Select a property"
                  allowClear
                  options={imageFieldOptions}
                  value={this.state.galleryViewPropertyNames?.image ?? null}
                  onChange={this.onChangeImageProperty}
                  onClear={(): void => this.onChangeImageProperty(null)}
                />
              )}
            {showsTitleProperty &&
              this.renderRow(
                "Title Property",
                <KuikaSelectbox
                  data-testid="titlePropertyDropdown"
                  data-onboarding-id="common_element-picker_title-property-dropdown"
                  placeholder="Select a property"
                  allowClear
                  options={titleFieldOptions}
                  value={this.state.galleryViewPropertyNames?.title ?? null}
                  onChange={this.onChangeTitleProperty}
                  onClear={(): void => this.onChangeTitleProperty(null)}
                />
              )}
          </div>
        </div>
      </KModal>
    );
  }
}

const elementPickerWithActionModal = withSD(ElementPickerWithActionModal);
export { elementPickerWithActionModal as ElementPickerWithActionModal };
